import { toast } from 'react-toastify'
import { getRuntimeConfig } from '@/libs/runtime-config'
import { encodeCstoreToken } from '@/libs/api/cstore-token'
import { emitSessionExpired } from '@/libs/session-expired'
import { touchSessionActivity } from '@/libs/session-idle'

export interface ApiResponse<T = any> {
  data?: T
  message?: string
  error?: string | string[]
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: any
  ) {
    super(statusText)
    this.name = 'ApiError'
  }
}

/** Keys we clear on 401 (session expired) */
const AUTH_STORAGE_KEYS = [
  'backend_jwt_token',
  'next-auth.session-token',
  'next-auth.csrf-token',
  '__Secure-next-auth.session-token',
  '__Secure-next-auth.csrf-token',
]

/** Endpoints that return 401 for invalid credentials; we do not treat as "session expired" or redirect. */
const AUTH_ENDPOINTS = ['/auth/login', '/auth/mfa/verify', '/auth/register', '/auth/forgot-password']

function isAuthEndpoint(endpoint: string): boolean {
  const path = endpoint.split('?')[0]
  return AUTH_ENDPOINTS.some((p) => path.endsWith(p) || path.includes(p + '/'))
}

/** Endpoints that must not reset the idle clock (login / logout). */
function isIdleTrackingExcluded(endpoint: string): boolean {
  const path = endpoint.split('?')[0]
  if (isAuthEndpoint(path)) return true
  return path.endsWith('/auth/logout') || path.includes('/auth/logout')
}

/** Endpoints where we never auto-toast a default success message (login/register/logout have their own UX — a redirect, a welcome screen, etc.). Explicit opts.showSuccessToast still wins if the caller really wants one. */
function isToastExcludedEndpoint(endpoint: string): boolean {
  return isAuthEndpoint(endpoint) || isIdleTrackingExcluded(endpoint)
}

/** Count authenticated API traffic so frontend idle logout matches backend sliding session. */
function trackApiActivity(endpoint: string, includeAuth: boolean): void {
  if (!includeAuth || isIdleTrackingExcluded(endpoint)) return
  touchSessionActivity()
}

/** Pull a human-readable message out of an error payload shaped like ApiResponse. */
function extractErrorMessage(data: any, fallback: string): string {
  if (!data) return fallback
  if (typeof data === 'string') return data || fallback
  if (Array.isArray(data.message)) return data.message.join(', ') || fallback
  if (typeof data.message === 'string' && data.message) return data.message
  if (typeof data.error === 'string' && data.error) return data.error
  if (Array.isArray(data.error)) return data.error.join(', ') || fallback
  return fallback
}

let handlingUnauthorized = false

/**
 * Handle 401 Unauthorized: clear token, show session-expired popup, then sign out.
 * Skipped for login/register so failed login does not trigger redirect.
 */
function handleUnauthorized(endpoint: string): void {
  if (isAuthEndpoint(endpoint)) return
  if (globalThis.window === undefined) return
  if (handlingUnauthorized) return
  handlingUnauthorized = true

  const client = (globalThis as any).__apiClientRef as ApiClient | undefined
  if (client) {
    client.clearToken()
  }

  try {
    localStorage.removeItem('backend_jwt_token')
    sessionStorage.removeItem('backend_jwt_token')
    for (const key of AUTH_STORAGE_KEYS) {
      localStorage.removeItem(key)
      sessionStorage.removeItem(key)
    }
  } catch (e) {
    console.warn('[API Client] Failed to clear some storage:', e)
  }

  emitSessionExpired()
}

/** Per-call toast overrides. */
export interface RequestOptions {
  includeAuth?: boolean
  /** Disable both success and error toasts for this call, regardless of method defaults. */
  silent?: boolean
  /** Force show/hide the success toast (overrides the method default, and the auth-endpoint exclusion). */
  showSuccessToast?: boolean
  /** Force show/hide the error toast (overrides the method default). */
  showErrorToast?: boolean
  /** Custom success message. Falls back to the method's default (e.g. 'Added Successfully' for POST). */
  successMessage?: string
  /** Fallback error message when the response has no message/error field. */
  errorFallbackMessage?: string
}

type HttpMethod = 'get' | 'getBlob' | 'post' | 'put' | 'patch' | 'delete'

/** Whether the success toast fires by default per method. GET/getBlob default off to avoid noise on every fetch. */
const DEFAULT_SUCCESS_TOAST: Record<HttpMethod, boolean> = {
  get: false,
  getBlob: false,
  post: true,
  put: true,
  patch: true,
  delete: true,
}

/** Default success message per method, used when the caller doesn't pass opts.successMessage. */
const DEFAULT_SUCCESS_MESSAGE: Record<HttpMethod, string> = {
  get: 'Success',
  getBlob: 'Success',
  post: 'Added Successfully',
  put: 'Updated Record Successfully',
  patch: 'Updated Record Successfully',
  delete: 'Removed Successfully',
}

function resolveToastFlags(method: HttpMethod, endpoint: string, opts: RequestOptions) {
  if (opts.silent) {
    return { showSuccessToast: false, showErrorToast: false }
  }

  const excluded = isToastExcludedEndpoint(endpoint)

  return {
    // Auth/logout endpoints don't get an auto default toast unless the caller explicitly asks for one.
    showSuccessToast: opts.showSuccessToast ?? (excluded ? false : DEFAULT_SUCCESS_TOAST[method]),
    // Errors still toast by default even on auth endpoints (e.g. "Invalid credentials"),
    // unless the caller explicitly turns it off.
    showErrorToast: opts.showErrorToast ?? true,
  }
}

class ApiClient {
  private readonly baseURL: string | null = null
  private token: string | null = null

  constructor(baseURL?: string) {
    // Allow explicit baseURL override, otherwise will be resolved dynamically
    if (baseURL) {
      this.baseURL = baseURL
    }

    // Purge legacy storage keys (ZAP: JWT in localStorage / sessionStorage)
    if (globalThis.window) {
      try {
        localStorage.removeItem('backend_jwt_token')
        sessionStorage.removeItem('backend_jwt_token')
      } catch {
        // ignore
      }
    }
  }

  /**
   * Get the base URL dynamically from runtime config
   * This ensures we always get the latest value from globalThis.__RUNTIME_CONFIG__
   */
  private getBaseURL(): string {
    // If explicitly set, use it
    if (this.baseURL) {
      return this.baseURL
    }

    // Otherwise, get from runtime config (supports globalThis.__RUNTIME_CONFIG__)
    // Default value ensures we always return a string
    return getRuntimeConfig('NEXT_PUBLIC_API_URL', 'http://localhost:5000') || 'http://localhost:5000'
  }

  /**
   * Set the backend token in memory only (never localStorage/sessionStorage).
   */
  setToken(token: string | null) {
    this.token = token
    if (token) {
      touchSessionActivity()
    }
    if (globalThis.window) {
      try {
        localStorage.removeItem('backend_jwt_token')
        sessionStorage.removeItem('backend_jwt_token')
      } catch {
        // ignore
      }
    }
  }

  /**
   * Clear the token (called on logout)
   */
  clearToken() {
    this.setToken(null)
  }

  /** In-memory token only (sync). */
  getToken(): string | null {
    return this.token
  }

  /**
   * Get the backend token from memory (synchronous).
   */
  private getTokenSync(): string | null {
    return this.token
  }

  /**
   * Get authorization headers.
   * Prefer in-memory opaque token (CstoreToken). Otherwise rely on Nest httpOnly
   * `access_token` cookie via credentials: 'include' (no session JSON token).
   */
  private async getHeaders(includeAuth: boolean = true): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (includeAuth) {
      const token = this.getTokenSync()
      if (token) {
        // Always opaque `cst1.…` — never send raw `eyJ…` JWT on the wire
        headers['CstoreToken'] = encodeCstoreToken(token)
      }
    }

    return headers
  }

  /** Apply sliding-session token from Nest response header. */
  private applySlidingSessionToken(response: Response): void {
    const refreshed = response.headers.get('cstoretoken')
    if (refreshed) {
      this.setToken(refreshed)
    }
  }

  /**
   * Handle API response. Toasts automatically based on method defaults / opts,
   * except for 401 (handled by the session-expired flow) and auth/logout
   * endpoints (which get no default success toast).
   */
  private async handleResponse<T>(
    response: Response,
    endpoint: string,
    method: HttpMethod,
    opts: RequestOptions = {}
  ): Promise<T> {
    const { showSuccessToast, showErrorToast } = resolveToastFlags(method, endpoint, opts)

    if (response.status === 401) {
      handleUnauthorized(endpoint)
      const contentType = response.headers.get('content-type')
      const isJson = contentType?.includes('application/json')
      const data = isJson ? await response.json().catch(() => ({})) : await response.text()
      throw new ApiError(response.status, response.statusText, data)
    }

    this.applySlidingSessionToken(response)

    const contentType = response.headers.get('content-type')
    const isJson = contentType?.includes('application/json')

    let data: any
    if (isJson) {
      data = await response.json()
    } else {
      data = await response.text()
    }

    if (!response.ok) {
      if (showErrorToast) {
        toast.error(extractErrorMessage(data, opts.errorFallbackMessage || 'Something went wrong.'))
      }
      throw new ApiError(response.status, response.statusText, data)
    }

    if (showSuccessToast) {
      toast.success(opts.successMessage || DEFAULT_SUCCESS_MESSAGE[method])
    }

    return data
  }

  /**
   * GET request
   */
  async get<T = any>(endpoint: string, includeAuth: boolean = true, opts: RequestOptions = {}): Promise<T> {
    trackApiActivity(endpoint, includeAuth)
    const headers = await this.getHeaders(includeAuth)
    const response = await fetch(`${this.getBaseURL()}${endpoint}`, {
      method: 'GET',
      headers,
      credentials: 'include', // Important for cookies
    })

    return this.handleResponse<T>(response, endpoint, 'get', opts)
  }

  /**
   * GET request that returns response as Blob (e.g. file export).
   */
  async getBlob(endpoint: string, includeAuth: boolean = true, opts: RequestOptions = {}): Promise<Blob> {
    trackApiActivity(endpoint, includeAuth)
    const headers = await this.getHeaders(includeAuth)
    const response = await fetch(`${this.getBaseURL()}${endpoint}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    })

    const { showSuccessToast, showErrorToast } = resolveToastFlags('getBlob', endpoint, opts)

    if (response.status === 401) {
      handleUnauthorized(endpoint)
      const contentType = response.headers.get('content-type')
      const isJson = contentType?.includes('application/json')
      const data = isJson ? await response.json().catch(() => ({})) : await response.text()
      throw new ApiError(response.status, response.statusText, data)
    }
    this.applySlidingSessionToken(response)
    if (!response.ok) {
      const contentType = response.headers.get('content-type')
      const isJson = contentType?.includes('application/json')
      const data = isJson ? await response.json() : await response.text()
      if (showErrorToast) {
        toast.error(extractErrorMessage(data, opts.errorFallbackMessage || 'Failed to download file.'))
      }
      throw new ApiError(response.status, response.statusText, data)
    }
    if (showSuccessToast) {
      toast.success(opts.successMessage || DEFAULT_SUCCESS_MESSAGE.getBlob)
    }
    return response.blob()
  }

  /**
   * POST request
   * When data is FormData, Content-Type is omitted so fetch sets multipart/form-data with boundary.
   */
  async post<T = any>(
    endpoint: string,
    data?: any,
    includeAuth: boolean = true,
    opts: RequestOptions = {}
  ): Promise<T> {
    trackApiActivity(endpoint, includeAuth)
    const isFormData = data instanceof FormData
    const headers = await this.getHeaders(includeAuth)

    if (isFormData) {
      // Do not set Content-Type for FormData; fetch will set multipart/form-data with boundary
      const { 'Content-Type': _ct, ...rest } = headers as Record<string, string>
      const response = await fetch(`${this.getBaseURL()}${endpoint}`, {
        method: 'POST',
        headers: rest,
        credentials: 'include',
        body: data,
      })
      return this.handleResponse<T>(response, endpoint, 'post', opts)
    }

    const response = await fetch(`${this.getBaseURL()}${endpoint}`, {
      method: 'POST',
      headers,
      credentials: 'include', // Important for cookies
      body: data ? JSON.stringify(data) : undefined,
    })

    return this.handleResponse<T>(response, endpoint, 'post', opts)
  }

  /**
   * PUT request
   */
  async put<T = any>(
    endpoint: string,
    data?: any,
    includeAuth: boolean = true,
    opts: RequestOptions = {}
  ): Promise<T> {
    trackApiActivity(endpoint, includeAuth)
    const headers = await this.getHeaders(includeAuth)
    const response = await fetch(`${this.getBaseURL()}${endpoint}`, {
      method: 'PUT',
      headers,
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
    })

    return this.handleResponse<T>(response, endpoint, 'put', opts)
  }

  /**
   * DELETE request
   */
  async delete<T = any>(endpoint: string, includeAuth: boolean = true, opts: RequestOptions = {}): Promise<T> {
    trackApiActivity(endpoint, includeAuth)
    const headers = await this.getHeaders(includeAuth)
    const response = await fetch(`${this.getBaseURL()}${endpoint}`, {
      method: 'DELETE',
      headers,
      credentials: 'include',
    })

    return this.handleResponse<T>(response, endpoint, 'delete', opts)
  }

  /**
   * PATCH request
   */
  async patch<T = any>(
    endpoint: string,
    data?: any,
    includeAuth: boolean = true,
    opts: RequestOptions = {}
  ): Promise<T> {
    trackApiActivity(endpoint, includeAuth)
    const headers = await this.getHeaders(includeAuth)
    const response = await fetch(`${this.getBaseURL()}${endpoint}`, {
      method: 'PATCH',
      headers,
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
    })

    return this.handleResponse<T>(response, endpoint, 'patch', opts)
  }
}

// Export singleton instance (baseURL will be resolved dynamically from runtime config)
export const apiClient = new ApiClient()
if (typeof globalThis !== 'undefined') {
  (globalThis as any).__apiClientRef = apiClient
}

// Export methods for external use
export const setApiToken = (token: string | null) => apiClient.setToken(token)
export const clearApiToken = () => apiClient.clearToken()
export const getApiToken = () => apiClient.getToken()